/* eslint-disable @typescript-eslint/no-explicit-any */
import apiRoutes from "@/src/apiRoutes";
import api from "@/src/utils";
import { useMutation } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Message, TradeComponentsData } from "../types";
import { textVariants } from "@/src/components/Text/Text";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback } from "@/src/components/shad/avatar";
import { useAppSelector } from "@/src/app/hooks";
import { ArrowUp, Image } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { isEmpty } from "lodash";
import { Modal } from "@/src/components/Modal/Modal";
import uploadSvg from "@assets/upload.svg";
import { Button } from "@/src/components/Button/Button";
import { Modal as ModalM } from "@mantine/core";
import moment from "moment";
import { useTranslation } from "react-i18next";

interface Props {
  partner: string;
  data: TradeComponentsData | undefined;
  // client: Centrifuge | null;
  messages: Message[];
}

const Chat = ({ partner, data, messages }: Props) => {
  const { t } = useTranslation();
  const trans = (key: string) => t(`app.p2p.chat.${key}`);
  const { id } = useParams();
  const { user } = useAppSelector((state) => state?.user);
  const [message, setMessage] = useState("");
  const [viewImage, setViewImage] = useState(false);
  const [imageToView, setImageToView] = useState("");
  const [openImage, setOpenImage] = useState(false);
  const [files, setFiles] = useState<Array<string>>([]);

  const imageRef = useRef<any>(null);
  const textareaRef = useRef<any>(null);

  const sendMessage = async (image?: string) => {
    const res = await api.post(`${apiRoutes.trade}${id}/`, {
      type: "submit_message",
      message_type: isEmpty(image) ? "text" : "image",
      message: isEmpty(image) ? message.trim() : image,
    });

    return res.data;
  };

  const { mutate: mutateSend, isLoading: sendLoad } = useMutation({
    mutationFn: sendMessage,
    onSuccess: () => {
      setMessage("");
    },
  });

  const toAws = async () => {
    const res = await api.post(apiRoutes.imageUpload, {
      path: "chat",
      images: files,
    });
    return res.data;
  };

  const { mutate: sendToAws, isLoading: awsLoad } = useMutation({
    mutationFn: toAws,
    onSuccess: (data) => {
      if (data?.success) {
        data?.info.forEach((item: string) => mutateSend(item));
        setOpenImage(false);
        setFiles([]);
      }
    },
  });

  // function to create base64 string
  const readAndCreate = (file: File) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onloadend = () => {
      const base64String = fileReader.result?.toString().split(",")[1];
      setFiles((prev) => [...prev, base64String as string]);
    };
  };

  // function to handle file change
  function handleFileChange(e: any) {
    const files = e.target.files;
    // Use the files as an array and call the readAndCreate function on each file mapped
    Array.prototype.forEach.call(files, readAndCreate);
  }

  const messagesEndRef = useRef<any>(null);

  //Scroll chat to bottom when new message is received and when chat is also opened
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  };

  useEffect(scrollToBottom, [data?.info?.messages, messages, id]);

  useEffect(() => {
    scrollToBottom();
    if (textareaRef.current) {
      // Reset textarea height to auto to correctly calculate scrollHeight
      textareaRef.current.style.height = "auto";
      // Set the height of the textarea based on the scrollHeight
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  return (
    <div className="bg-white dark:bg-grey-500   lg:top-0  overflow-scroll rounded-xl flex flex-col h-full md:max-h-[70vh] max-h-[80vh]">
      <div className="px-3 flex justify-between py-2 border-b border-gray-200 dark:border-grey-400">
        <div className="flex items-center justify-between gap-2">
          {/* <Avatar color="" variant={'filled'} radius={'xl'}>{partner[0]}</Avatar> */}
          <Avatar className="p-1">
            {/* <AvatarImage src="https://github.com/shadcn.png" /> */}
            <AvatarFallback className="bg-[#2F60E0] p-1 text-white">
              {partner[0]}
            </AvatarFallback>
          </Avatar>
          <span className={textVariants({ font: "semibold" })}>{partner}</span>
        </div>
        <div className="flex items-center">
          <span className="flex items-center gap-2">
            <span
              className={cn(
                "p-1 rounded-full",
                data?.info?.online_status.online ? "bg-green-500" : "bg-red-500"
              )}
            ></span>
            <span
              className={textVariants({
                size: "xs",
                variant: "desc",
                font: "semibold",
              })}
            >
              {data?.info?.online_status.online
                ? trans("status.online")
                : trans("status.offline")}
            </span>
          </span>
        </div>
      </div>
      <div ref={messagesEndRef} className="flex-1 gap-20 overflow-y-scroll ">
        <div className="grid gap-10 overflow-y-scroll p-5">
          {data &&
            [...data.info.messages, ...messages]?.map(
              (mes: Message, key: number) => (
                <div
                  key={key}
                  // dir={mes?.sender__username === user?.username ? "rtl" : "ltr"}
                  className={cn(
                    "flex relative",
                    mes?.sender__username === user?.username
                      ? "justify-end"
                      : "justify-start"
                  )}
                >
                  {mes?.sender__username !== user?.username && (
                    <div>
                      <Avatar className="p-1">
                        <AvatarFallback className="bg-[#2F60E0] p-1 text-white">
                          {mes?.sender__username[0]}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                  )}
                  <span
                    className={cn(
                      textVariants({ size: "sm" }),
                      "md:max-w-[60%] bg-slate-200 dark:bg-grey-700 p-2 grid",
                      mes?.sender__username === user?.username
                        ? "rounded-l-xl rounded-br-xl"
                        : "rounded-r-xl rounded-bl-xl",
                      mes?.sender__username.toLowerCase() === "mybitstore" &&
                        " bg-infoblue-100 dark:bg-grey-400 w-full"
                    )}
                  >
                    {mes.message_type === "image" ? (
                      <img
                        onClick={() => [
                          setImageToView(mes?.message),
                          setViewImage(true),
                        ]}
                        src={mes?.message}
                        alt=""
                      />
                    ) : (
                      <pre className=" whitespace-break-spaces">
                        {mes?.message}
                      </pre>
                    )}
                    {mes?.sender__username.toLowerCase() === "mybitstore" && (
                      <span
                        className={textVariants({
                          variant: "link",
                          size: "xs",
                          font: "semibold",
                        })}
                      >
                        Mybitstore
                      </span>
                    )}
                  </span>
                  {mes?.sender__username === user?.username && (
                    <div>
                      <Avatar className="p-1">
                        <AvatarFallback className="bg-[#2F60E0] p-1 text-white">
                          {mes?.sender__username[0]}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                  )}
                  <span
                    className={cn(
                      "absolute -bottom-5 text-xs dark:text-grey-200",
                      mes?.sender__username === user?.username
                        ? "right-8"
                        : "left-10"
                    )}
                  >
                    {moment(mes?.created_at).format("LT")}
                  </span>
                </div>
              )
            )}
        </div>
      </div>
      <div className="flex py-1">
        <div className="flex gap-2 w-full items-center px-2">
          <textarea
            ref={textareaRef}
            onChange={(e) => setMessage(e.target.value)}
            value={message}
            rows={1}
            placeholder={trans("status.inputPlaceholder")}
            className="bg-gray-200/60 dark:bg-grey-800 resize-none overflow-hidden dark:text-white w-full min-h-[50px] box-border ring-0 outline-none focus:border-0 p-3 rounded-xl text-sm "
          />
          {!isEmpty(message.trim()) && (
            <button
              onClick={() => {
                mutateSend("");
              }}
              disabled={sendLoad || isEmpty(message.trim())}
              className="rounded-full bg-blue-600  p-2 text-gray-600 cursor-pointer hover:bg-blue-500 dark:bg-grey-700 dark:text-white hover:dark:bg-grey-600"
            >
              <ArrowUp className="h-4 w-4 text-white" />
            </button>
          )}
          <Image
            onClick={() => setOpenImage(true)}
            className="text-gray-600 h-6 w-6 flex cursor-pointer dark:text-gray-200"
          />
        </div>
      </div>

      <Modal
        open={openImage}
        setOpen={setOpenImage}
        setter="state"
        footer={
          <div>
            <Button
              disabled={isEmpty(files)}
              onClick={() => sendToAws()}
              loading={awsLoad || sendLoad}
            >
              {trans("image.uploadButton")}
            </Button>
          </div>
        }
      >
        <div>
          <input
            ref={imageRef}
            multiple
            onChange={handleFileChange}
            type="file"
            className="hidden"
            accept="image/*"
            id=""
          />
          {isEmpty(files) && (
            <div>
              <div
                onClick={() => imageRef.current.click()}
                className="flex items-center flex-col cursor-pointer"
              >
                <img src={uploadSvg} className="h-32 w-full" alt="" />
                <span
                  className={cn(
                    textVariants({ variant: "desc" }),
                    "cursor-pointer"
                  )}
                >
                  {trans("image.description")}
                </span>
              </div>
            </div>
          )}
          {!isEmpty(files) && (
            <div className="w-full">
              <div className="flex items-center gap-5">
                {files.map((item) => (
                  <img
                    src={`data:image/jpeg;base64,${item}`}
                    className="h-32 w-32 rounded-md border border-gray-200"
                    alt="img"
                  />
                ))}
                <div
                  onClick={() => imageRef.current.click()}
                  className="h-14 w-14 flex flex-col items-center  justify-end"
                >
                  <Image className="text-gray-600 dark:text-gray-200 h-8 w-8 flex cursor-pointer" />
                  <span className="text-[10px] dark:text-white">
                    {trans("image.addImage")}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>
      <ModalM
        opened={viewImage}
        onClose={() => setViewImage(false)}
        centered
        size={"xl"}
        title="View Image"
        // fullScreen
      >
        <div className="flex  items-center justify-center">
          <img src={imageToView} alt="" className="max-h-[90vh] h-full" />
        </div>
      </ModalM>
    </div>
  );
};

export default Chat;
